/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BaseStepCompProps, ConfigFormData } from './types';
import { OnboardingStep } from '@g17eco/types/common';
import { VerifyEmail } from './steps/VerifyEmail';
import { Completed } from './steps/Completed';
import { Signup } from './steps/Signup';
import { CompanyInfo } from './steps/CompanyInfo';
import { EmailDomainCompanyOnboardOrNot } from './steps/EmailDomainCompanyOnboardOrNot';
import './styles.scss';
import { ReferralInfo } from './steps/ReferralInfo';
import { useAppSelector } from '../../reducers';
import { getCurrentUser } from '../../selectors/user';
import { AppConfig } from '../../types/app';
import { BasicAlert } from '@g17eco/molecules/alert';
// oxlint-disable-next-line g17eco/no-restricted-paths
import { CompletedMAT, CreateAssessmentForm } from '@apps/materiality-tracker/components/onboarding/steps';
// oxlint-disable-next-line g17eco/no-restricted-paths
import { CompletedET } from '@apps/emissions-tracker/components/onboarding/steps/CompletedET';
import { CompanyInfoForm, CompanyInfoFormMAT } from '@features/onboarding/partials/CompanyInfoForm';
import { CompanyInfoFormProps } from '@features/onboarding/hooks/useGetCompanyInfo';

interface OnboardingStepFormProps {
  appConfig: AppConfig;
  handlePrevious?: () => void;
  handleNext: () => void;
  goToStep: (step?: OnboardingStep) => void;
  activeStep: OnboardingStep;
  referralInfo: {
    referralCode: string | null;
    referralName: string | null;
  };
}

export const OnboardingStepForm = (props: OnboardingStepFormProps) => {
  const { appConfig, activeStep, handleNext, handlePrevious, referralInfo, goToStep } = props;

  const currentUser = useAppSelector(getCurrentUser);
  const [formData, setFormData] = useState<ConfigFormData>({
    firstName: currentUser?.firstName ?? '',
    surname: currentUser?.surname ?? '',
    password: '',
    rPassword: '',
    email: currentUser?.email ?? '',
    telephoneNumber: currentUser?.telephoneNumber ?? '',
    initiativeName: '',
    initiativeCountryCode: '',
    registrationNumber: '',
    referenceCompany: referralInfo.referralName ?? currentUser?.registrationData?.onboardingData?.referenceCompany ?? '',
    referralCode: referralInfo.referralCode ?? currentUser?.registrationData?.onboardingData?.referralCode ??'',
    industry: {},
    financialEndDate: { month: '-', day: '-' },
  });

  useEffect(() => {
    // User might not have been loaded yet, try it to populate the field if available now
    const obData = currentUser?.registrationData?.onboardingData;
    if (!obData) {
      return;
    }

    const { referralCode, referenceCompany } = obData
    if (referralCode || referenceCompany) {
      setFormData((f) => {
        // Only update fields if not yet set
        const update: Partial<ConfigFormData> = {};
        if (!f.referralCode && obData.referralCode) {
          update.referralCode = obData.referralCode;
        }
        if (!f.referenceCompany && obData.referenceCompany) {
          update.referenceCompany = obData.referenceCompany;
        }
        return Object.keys(update).length ? { ...f, ...update } : f;
      })
    }
  }, [currentUser]);

  const updateForm = useCallback((name: string, value: any) => {
    setFormData((form) => ({
      ...form,
      [name]: value,
    }));
  }, [setFormData]);

  const stepProps: BaseStepCompProps = {
    appConfig,
    updateForm,
    goToStep,
    handleNext,
    handlePrevious,
    formData,
  }

  switch (activeStep) {
    case OnboardingStep.Signup:
      return <Signup {...stepProps} />
    case OnboardingStep.VerifyEmail:
      return <VerifyEmail {...stepProps} />
    case OnboardingStep.CompanyInfo:
      return <CompanyInfo {...stepProps} renderForm={(props: CompanyInfoFormProps) => <CompanyInfoForm {...props} />} />;
    case OnboardingStep.ReferralInfo:
      return <ReferralInfo {...stepProps} />
    case OnboardingStep.EmailDomainCompanyOnboardOrNot:
      return <EmailDomainCompanyOnboardOrNot {...stepProps} />;
    case OnboardingStep.Complete:
      return <Completed appConfig={appConfig} />
    case OnboardingStep.CompleteMAT:
      return <CompletedMAT appConfig={appConfig} />;
    case OnboardingStep.CompleteET:
      return <CompletedET appConfig={appConfig} />;
    case OnboardingStep.CreateAssessment:
      return <CreateAssessmentForm {...stepProps} />;
    case OnboardingStep.CompanyInfoMAT:
      return <CompanyInfo {...stepProps} renderForm={(props: CompanyInfoFormProps) => <CompanyInfoFormMAT {...props} />} />;
    default:
      return <BasicAlert type='danger'>
        There was a problem rendering the requested page ({props.activeStep}). Please refresh the page and try again.
      </BasicAlert>;
  }
};
